import { useMemo } from 'react';

// Function to determine base axis type, scale, and domain
const getBaseAxisProperties = (config, data, dataKey) => {
  if (config?.axisConfig?.categoryAxis?.type === 'category') {
    return { scale: "band", type: "category", domain: undefined };
  } else if (config?.axisConfig?.categoryAxis?.type === 'time' || config?.axisConfig?.categoryAxis?.type === 'date') {
    return { scale: "time", type: "number", domain: ['dataMin', 'dataMax'] };
  }

  if (!data || data.length === 0) {
    return { scale: "band", type: "category" };
  }

  const firstPoint = data[0];
  const xValue = firstPoint?.[dataKey];

  if (typeof xValue === 'number' && xValue > 1000000000) { // Likely timestamp
    return { scale: "time", type: "number", domain: ['dataMin', 'dataMax'] };
  } else if (typeof xValue === 'string') {
    return { scale: "band", type: "category" };
  }

  // Default fallback
  return { scale: "band", type: "category" };
};

export const getAxisConfiguration = (
  config,
  data,
  chartOrientation,
  timeGrouping,
  tickRenderer // Accept the pre-defined tick renderer component/function
) => {
  const axisConfig = config?.axisConfig || {};
  const categoryAxisConfig = axisConfig.categoryAxis || {};
  const dataKey = categoryAxisConfig.dataKey || 'label';
  const dataLength = data?.length || 0;

  // Determine base properties (type, scale, domain)
  const baseProps = getBaseAxisProperties(config, data, dataKey);

  // Calculate interval based on data length
  const calculateInterval = () => {
    if (categoryAxisConfig.interval !== undefined) return categoryAxisConfig.interval;
    
    // Always show all labels for category axes
    if (baseProps.type === 'category') {
      return 0;
    }
    
    // Keep existing logic for other axis types (e.g., time/date)
    if (dataLength > 60) return Math.floor(dataLength / 15);
    if (dataLength > 24) return Math.floor(dataLength / 12);
    return 0;
  };

  const interval = calculateInterval();

  // Common properties derived from baseProps and categoryAxisConfig
  const commonProps = {
    dataKey: dataKey,
    type: baseProps.type,
    scale: baseProps.scale === 'time' ? 'time' : undefined,
    domain: baseProps.domain,
    tick: tickRenderer, // Use the passed tickRenderer directly
    interval: interval,
    padding: { left: 15, right: 15, top: 15, bottom: 15 }
  };

  // Orientation-specific properties
  if (chartOrientation === 'vertical') {
    const isDateAxis = categoryAxisConfig.type === 'date' || categoryAxisConfig.type === 'time';
    const shouldAngleLabels = !isDateAxis && (baseProps.type === 'category' && (dataLength > 5 || categoryAxisConfig.forceAngleLabels));
    
    const anchor = isDateAxis 
      ? 'end' // Date axis labels are already rotated in the renderer
      : (shouldAngleLabels ? 'end' : 'middle');
      
    const angle = isDateAxis 
      ? 0 // Rotation is handled inside the tickRenderer component for date axis
      : (shouldAngleLabels ? -45 : 0);
      
    // Explicitly set height/interval/minTickGap for date axis for consistency with previous behavior
    const dateAxisOverrides = isDateAxis ? {
        height: 100,
        interval: 0,
        minTickGap: -200, // Prevent hiding ticks due to space
        tickLine: { stroke: '#999', strokeWidth: 1 },
        axisLine: { stroke: '#333', strokeWidth: 1 },
        tickSize: 8
    } : {};
      
    return {
      ...commonProps,
      height: categoryAxisConfig.height || (isDateAxis ? 100 : 80), // Use specific date height
      angle: categoryAxisConfig.angle ?? angle, // Use determined angle
      textAnchor: categoryAxisConfig.textAnchor ?? anchor, // Use determined anchor
      dy: categoryAxisConfig.dy ?? (isDateAxis ? 0 : 16), // Adjust dy for date axis if needed
      ...dateAxisOverrides // Apply date axis specific overrides
    };
  } else { // Horizontal orientation
    return {
      ...commonProps,
      width: categoryAxisConfig.width || 150,
    };
  }
};

export const useMonthReferenceLines = (data, config, axisType) => {
  return useMemo(() => {
    if (!data || data.length < 2 || config?.hideReferenceLines || axisType !== 'number') {
      return [];
    }
    
    const months = {};
    const result = [];
    
    const sortedData = [...data].sort((a, b) => {
      const getDateValue = (item) => {
        if (typeof item.date === 'number') return item.date;
        if (item.year && item.month) return new Date(item.year, item.month - 1, 1).getTime();
        return 0;
      };
      
      return getDateValue(a) - getDateValue(b);
    });
    
    sortedData.forEach(item => {
      if (!item.date && !(item.year && item.month)) return;
      
      const date = typeof item.date === 'number' 
        ? new Date(item.date)
        : new Date(item.year, item.month - 1, 1);
      
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      
      if (!months[monthKey] && Object.keys(months).length > 0) {
        const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
        result.push({
          date: firstOfMonth,
          label: date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
        });
      }
      
      months[monthKey] = true;
    });
    
    return result;
  }, [data, config?.hideReferenceLines, axisType]);
}; 